import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import type { McpClientId } from "@arcadeai/ui-kit/mcp-gateway-connect";
import { X } from "lucide-react";
import type { JSX } from "react";
import type { OnboardingGateway } from "@/app/onboarding/api/get-onboarding-progress";
import { OnboardingMobileTopBar } from "@/app/onboarding-first-win/components/onboarding-mobile-top-bar";
import { OnboardingOverlaySkeleton } from "@/app/onboarding-first-win/components/onboarding-overlay-skeleton";
import { OnboardingSidebar } from "@/app/onboarding-first-win/components/onboarding-sidebar";
import { useOnboardingOverlayState } from "@/app/onboarding-first-win/hooks/use-onboarding-overlay-state";
import type { OnboardingStep } from "@/app/onboarding-first-win/lib/onboarding-shared";
import { ConnectStep } from "@/app/onboarding-first-win/steps/connect/connect-step";
import { JtbdPicker } from "@/app/onboarding-first-win/steps/jtbd-picker/jtbd-picker";
import { NextStepsStep } from "@/app/onboarding-first-win/steps/next-steps/next-steps-step";
import { Loader } from "@/components/custom/loader";

export function OnboardingOverlay(): JSX.Element {
  const {
    handleSkip,
    sidebarSteps,
    isStepLoading,
    step,
    gateway,
    clientForNav,
    onSelectClient,
  } = useOnboardingOverlayState();

  if (isStepLoading) {
    return <OnboardingOverlaySkeleton onSkip={handleSkip} />;
  }

  return (
    <div className="flex h-svh flex-col bg-background md:flex-row">
      <OnboardingMobileTopBar currentStepId={step} steps={sidebarSteps} />
      <aside className="hidden w-72 shrink-0 flex-col border-border border-r bg-sidebar text-sidebar-foreground md:flex">
        <OnboardingSidebar
          footer={
            <Button
              className="-ml-2.5 text-muted-foreground/60 text-xs italic transition-colors hover:text-muted-foreground"
              onClick={handleSkip}
              size="sm"
              type="button"
              variant="ghost"
            >
              <X className="h-3 w-3" />
              Skip setup
            </Button>
          }
          steps={sidebarSteps}
        />
      </aside>
      <main className="flex min-h-0 flex-1 flex-col">
        <OnboardingStepContent
          clientForNav={clientForNav}
          gateway={gateway}
          isStepLoading={isStepLoading}
          onSelectClient={onSelectClient}
          step={step}
        />
        <div className="flex shrink-0 justify-center py-4 md:hidden">
          <Button
            className="text-muted-foreground/60 text-xs italic transition-colors hover:text-muted-foreground"
            onClick={handleSkip}
            size="sm"
            type="button"
            variant="ghost"
          >
            <X className="h-3 w-3" />
            Skip setup
          </Button>
        </div>
      </main>
    </div>
  );
}

function OnboardingStepContent({
  isStepLoading,
  step,
  gateway,
  clientForNav,
  onSelectClient,
}: {
  isStepLoading: boolean;
  step: OnboardingStep;
  gateway: OnboardingGateway | null;
  clientForNav: McpClientId | undefined;
  onSelectClient: (client: McpClientId | undefined) => void;
}): JSX.Element | null {
  if (isStepLoading) {
    return <CenteredLoader />;
  }
  switch (step) {
    case "jtbd":
      return <JtbdPicker />;
    case "connect":
      if (!gateway) {
        return <CenteredLoader />;
      }
      return (
        <ConnectStep
          clientForNav={clientForNav}
          gateway={gateway}
          onSelectClient={onSelectClient}
        />
      );
    case "done":
      return <NextStepsStep />;
    default:
      return null;
  }
}

function CenteredLoader(): JSX.Element {
  return (
    <div className="grid h-full flex-1 place-items-center">
      <Loader />
    </div>
  );
}
